import { useEffect, useState, FunctionComponent } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import Collections from './routes/Collections';
import { ThemeProvider } from '@mui/material/styles';
import useSystemTheme from 'hooks/useSystemTheme';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import localAuth, {AuthState} from './model/auth/AuthStore';
import usePlan from './model/auth/PlanStore';
import AppMenu from './components/nav/AppMenu'
import GettingStarted from './routes/GettingStarted';
import AboutUs from 'routes/AboutUs';
import PrivacyPolicy from 'routes/PrivacyPolicy';
import TermsOfService from 'routes/TermsOfService';
import ContactUs from 'routes/ContactUs';
import Notifications from 'routes/Notifications';
import Collection from 'routes/Collection';
import Default from 'routes/Default';
import EmailVerified from 'routes/EmailVerified';
import { jwtDecode } from 'jwt-decode';
import { log } from 'utils/Common';
import Dashboard from 'routes/Dashboard';
import NotFound from 'routes/NotFound';

import 'assets/light.css';
import 'assets/dark.css';
import 'assets/common.css';
import 'assets/home.css';

/**
 * if (user not logged in) we show the HomePage
 * if (user logged in && email is verified) we call the /user/login, and then load the dashboard
 * if (user logged in && email not verified) we show that a notification is sent to verify the email
 * 
 * the default.tsx handles isLoading, isAuthenticated, token, emailVerified
 */
export const App: FunctionComponent = () => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const updateAuthToken = localAuth((state: AuthState) => state.updateAuthToken);
  const [token, setToken] = useState("");
  const updateEmailVerified = localAuth((state: AuthState) => state.updateEmailVerified);
  const userLogin = usePlan((state) => state.userLogin);
  const [emailVerified, setEmailVerified] = useState(false);
  const [isFetchingUserInfo, setIsFetchingUserInfo] = useState(false);
  const theme = useSystemTheme();

  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated || isLoading) return;
      
      setIsFetchingUserInfo(true); // Set loading state
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            scope: "openid email",
            audience: process.env.REACT_APP_AUTH0_AUDIENCE
          }
        });
        
        updateAuthToken(accessToken);
        setToken(accessToken);

        const decoded: any = jwtDecode(accessToken);
        const emailVerified = decoded['https://collecting.cash/email_verified'];
        log(`email_verified: ${emailVerified}`);

        if (emailVerified) {
          await userLogin(accessToken);
          updateEmailVerified(true);
          setEmailVerified(true);
        } else {
          updateEmailVerified(false);
          setEmailVerified(false);
        }
      } catch (error) {
        console.error("Error fetching token:", error);
      } finally {
        setIsFetchingUserInfo(false); // Clear loading state
      }
    };
    fetchToken();
  }, [isAuthenticated, isLoading]); 

  // The loading check will ensure that we know if the user is authenticated
  // We check if the user is authenticated to decide whether to show home
  // Otherwise with wait for the token
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <AppMenu/>
        <Routes>
          <Route path="/" element={
            <Default emailVerified={emailVerified} isLoading={isLoading || isFetchingUserInfo} isAuthenticated={isAuthenticated} token={token} component={<Dashboard />}/>
          } />
          <Route path="/notifications" element={
            <Default emailVerified={emailVerified} isLoading={isLoading || isFetchingUserInfo} isAuthenticated={isAuthenticated} token={token} component={<Notifications />}/>
          } />
          <Route path="/collections" element={
            <Default emailVerified={emailVerified} isLoading={isLoading || isFetchingUserInfo} isAuthenticated={isAuthenticated} token={token} component={<Collections />}/>
          } />
          <Route path="/collection" element={
            <Default emailVerified={emailVerified} isLoading={isLoading || isFetchingUserInfo} isAuthenticated={isAuthenticated} token={token} component={<Collection />}/>
          } />
           
          <Route path="/getting-started" element={<GettingStarted/>} />
          <Route path="/about" element={<AboutUs/>} />
          <Route path="/contact" element={<ContactUs/>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy/>} />
          <Route path="/terms-of-service" element={<TermsOfService/>} />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  )
}
export default App;